import {
  ActionPlanTaskReorderPosition,
  ActionPlanTaskStatus,
  ActionPlanVisibility,
} from '../enums/ActionPlans';
import { BillingPeriod } from '../enums/Subscription/BillingPeriods';
import { CollaboratorType } from '../enums/CollaboratorType';
import { ContentIndexingStatus } from '../enums/Content/ContentIndexingStatus';
import {
  ContentProcessingStatus,
  ContentProcessors,
} from '../enums/ContentProcessingLogs';
import { ConversationMentionType } from '../enums/Conversations';
import { DealStageType } from '../enums/Integrations/DealStageType';
import { DeviceType } from '../enums/Device';
import { FromNamePreference } from '../enums/FromNamePreference';
import { GeoTargetConsent } from '../enums/GeoTargetConsent';
import { IntegrationEventType } from '../enums/Integrations/IntegrationEventType';
import { IntegrationLogTransactionStatus } from '../enums/IntegrationLogTransactionStatus';
import { IntegrationProvider } from '../enums/Integrations/IntegrationProvider';
import {
  IntegrationOwnerType,
  IntegrationType,
} from '../enums/Integrations/IntegrationType';
import { InteractionEvent } from '../enums/InteractionEvent';
import { MtContentData } from '../enums/Content';
import { OrganizationAssetType } from '../enums/OrganizationAssetType';
import { OrganizationStatus } from '../enums/OrganizationStatus';
import { PaginatedResourceInput } from '../graphql/Pagination';
import { Permission, PermissionValue } from '../enums/Permission';
import { ReplyToPreference } from '../enums/ReplyToPreference';
import { ReportType } from '../enums/ReportType';
import { RequestedReferenceStatus } from '../enums/RequestedReferenceStatus';
import { RoomAclType } from '../enums/RoomAclType';
import { RoomAttachmentDiscussionType } from '../enums/RoomAttachment';
import { RoomAudience } from '../enums/RoomAudience';
import { RoomGuestReminderStatus } from '../enums/RoomGuestReminderStatus';
import { RoomLayout } from '../enums/RoomLayout';
import {
  RoomObjectAclGuestType,
  RoomObjectAclType,
} from '../enums/RoomObjectAcl';
import { RoomStatus } from '../enums/RoomStatus';
import { RoomStructureItemType } from '../enums/RoomStructureItemType';
import { SignableFieldType } from '../enums/SignableFieldType';
import { SignableProviders } from '../enums/SignableProvider';
import {
  SignableStatus,
  SignableSyncStatus,
  SignerStatus,
} from '../enums/Signables';
import {
  StorageProviderAuthShareType,
  StorageProviderFileType,
} from '../enums/StorageProvider';
import { TrackEvent } from '../enums/Content/TrackEvent';
import { TrailLogEntityType, TrailLogEvent } from '../enums/TrailLogs';
import { UserActivityEvent } from '../enums/UserActivity';

export interface SalesActionSchema {
  id: string;
  name: string;
  label: string;
  personal: boolean;
  singleUse: boolean;
  url: string;

  // nullable
  createdById?: string;
  /**
   * Convenience field to properly handle single use sales actions in rooms
   * @note this value does not belong to the api schema. It's the only way
   * workaround for a series of complex scenarios regarding single use
   * sales actions in rooms.
   */
  singleUseValue?: boolean;
}

// Schema for the configuration of default content attributes for the organization
export interface DefaultContentAttributeSchema {
  id: string;
  key: string;
  multiple: boolean;
  options: ContentAttributeOptionSchema[];
  // new structure uses values instead of options
  values?: ContentAttributeValueSchema[];
}

// Schema for a content's actual attributes. This is different since it's
// stored as JSON metadata instead of relationships between normalized tables
export type ContentAttributesSchema = {
  [key: string]: string[];
};

export interface ContentAttributeOptionSchema {
  id: string;
  value: string;
  label: string;
}

export interface ContentAttributeValuesSchema {
  key: string;
  values: string[];
}

export interface MemberSchema {
  id: string;
  coverKey?: string;
  fromName?: string;
  jobTitle?: string;
  lastActivityAt?: number;
  monthlyRecapEmail?: boolean;
  organization?: OrganizationSchema;
  roleDescription?: string;
  signedCover?: string;
  user?: UserSchema;
  vendorUserId?: string;
  showInactives?: boolean;
  ownedPublishedDraftRooms?: number;
}

// Integrations
export interface IntegrationLogTransactionSchema {
  createdAt: number;
  results: string; // json object
  source: string;
  status: IntegrationLogTransactionStatus;
  transactionId: string;
}

export interface CrmIntegrationConfigurationDealStageSchema {
  label: string;
  order: number;
  value: string;
  stageType?: DealStageType;
}

export interface CrmIntegrationConfigurationDealSchema {
  label: string;
  order: number;
  value: string;
}

export interface CrmIntegrationDealNotificationSchema {
  minAmount?: number | null;
  sendNotification?: boolean;
  stages?: string[];
  types?: string[];
  userId?: string | null;
  userSource?: string;
  isDisabled?: boolean;
}

export interface CrmIntegrationConfigurationSchema {
  dealStages?: CrmIntegrationConfigurationDealStageSchema[];
  dealTypes?: CrmIntegrationConfigurationDealSchema[];
  notification?: CrmIntegrationDealNotificationSchema;
  notifyMapTaskCompleted?: boolean;
  notifyNewComment?: boolean;
  notifyRoomCreation?: boolean;
  notifyRoomEdit?: boolean;
  notifyRoomVisit?: boolean;
  notifySharedLinkVisit?: boolean;
  notifyEsignatureActivity?: boolean;
  provisionContactWhenNotifying?: boolean;
  uploadCompletedEsignature?: boolean;
}

export interface NotificationEventConfiguration {
  events: IntegrationEventType[];
  id: string;
  roomNotificationChannelId: string | null;
  sendDirectMessage: boolean;
}

export interface NotificationIntegrationConfigurationSchema {
  notifications: NotificationEventConfiguration[];
}

export interface IntegrationConfigurationChannelSchema {
  id: string;
  label: string;
}

export interface IntegrationConfigurationOptionsSchema {
  channels?: IntegrationConfigurationChannelSchema[];
}

export interface InstallableIntegrationSchema {
  providerName: IntegrationProvider;
  integrationType: IntegrationType;
  installUrl: string;
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/integrations/integration_type.rb
 */
export interface IntegrationSchema {
  id: string;
  createdAt: number;
  integrationType: IntegrationType;
  logs: IntegrationLogTransactionSchema[];
  ownerType?: IntegrationOwnerType;
  providerName: IntegrationProvider;
  updatedAt: number;
  version: string;
  // nullable
  configuration?:
    | CrmIntegrationConfigurationSchema
    | NotificationIntegrationConfigurationSchema;
  connectionName?: string;
  deactivated?: boolean;
  metadata?: { connection_variant?: ConnectionVariant };
  notificationUser?: UserSchema;
}

export interface IntegrationAuthSchema {
  id: string;
  label: string;
  provider: IntegrationProvider;
}

export interface OrganizationColorsSchema {
  autopickSecondary: boolean;
  primary: string;
  secondary: string;
  icon: string;
}

export interface FontVariantSchema {
  name: string;
  value: string;
}

export interface FontSchema {
  family: string;
  standard?: boolean;
  weight?: string;
}

export interface OrganizationFontsSchema {
  body?: FontSchema;
  headline?: FontSchema;
}

export interface OrganizationLibraryFiltersSchema {
  id: string | null;
  label?: string;
  type: string;
  visible: boolean;
}

export interface OrganizationSettingsSchema {
  scheduleCall: boolean;

  // nullable
  actionPlanLabel?: string;
  bannerAssetId?: string;
  bannerUri?: string;
  buyerCopilotEnabled?: boolean;
  colors?: OrganizationColorsSchema;
  consentContactEnrichmentEvents?: EventSchema[];
  crmIntegration?: IntegrationSchema;
  defaultContentDownload?: boolean;
  disableRoomStickyColumns?: boolean;
  disableSort?: boolean;
  domainBlacklist?: string[];
  domainWhitelist?: string[];
  fonts?: OrganizationFontsSchema;
  fullTextSearch?: boolean;
  libraryFilters?: OrganizationLibraryFiltersSchema[];
  qaLogo?: string;
  website?: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/vendor_type.rb
 */
export interface VendorSchema {
  id: string;
  homeUrl: string;
  name: string;
  emailSenderName: string;

  // nullable
  analyticsPrefix?: string;
  pdfViewerLicenseKey?: string;
  privacyPolicyLink?: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/organization_type.rb
 */
export interface OrganizationSchema extends OrganizationSettingsSchema {
  id: string;
  activeUsersCount: number;
  contentIndexingStatus: ContentIndexingStatus;
  createdAt: number;
  name: string;
  phone: string;
  status: OrganizationStatus;
  totalUsersCount: number;
  vendorId: number;
  tenantType?: string;
  primaryOrganization: OrganizationSchema;

  // Read-only Salesforce field that populates the deal amount. Nullable —
  // null/absent means no override is set (consumer falls back to "Amount").
  amountSourceField?: string | null;

  // flags
  releaseControlPartnerSettings: boolean;
  crmInfoSearch: boolean;
  crmIntegrationFeature: boolean;
  dataEnrichment: boolean;
  disableDealCreation: boolean;
  disableCompanyCreation: boolean;
  disableStartConversation: boolean;
  autoRoomCreation: boolean;
  federatedAssetEnabled: boolean;
  inlineIframeViewer: boolean;
  editCopy: boolean;
  emailSenderConfigurable: boolean;
  forceRoomsDealCreation: boolean;
  allowSkipDeal: boolean;
  fromNamePreference: FromNamePreference;
  fullTextSearch: boolean;
  insightsOptimizationV2: boolean;
  geoTargetConsent: GeoTargetConsent;
  gongStorageProvider: boolean;
  hidePoweredBy: boolean;
  inhouseConsent: boolean;
  inhouseSigner: boolean;
  insightsContentWonDeals: boolean;
  mutualActionPlans: boolean;
  newPdfViewer: boolean;
  newRoomLayouts: boolean;
  openAi: boolean;
  referenceManagement: boolean;
  replyToPreference: ReplyToPreference;
  ringCentral: boolean;
  ringcentralStorageProvider: boolean;
  featureFlagSupportMulticurrencySfdc: boolean;
  consentDeclinedSuppression: boolean;
  releaseControlBuyerCopilot: boolean;
  releaseControlDsrFolders: boolean;
  roomBlacklistDomains: boolean;
  sharedLinks: boolean;
  showAllCollaborators: boolean;
  signablesFeature: boolean;
  signableTemplates: boolean;
  isUserHierarchyConfigured: boolean;
  smsNotifications: boolean;
  vanityUrlFeature: boolean;
  zoomStorageProvider: boolean;
  enforcedAllowedViewersOnly?: boolean;
  enforcedRequiredPin?: boolean;
  enforcedRequiredEmail?: boolean;
  featureCrmAbstractionMsDynamics?: boolean;

  releaseControlRestrictUserVisibility: boolean;

  // rollout flags
  internalRoomsV2: boolean;

  // nullable
  address?: string;
  allowSupportLogin?: boolean;
  bannerUri?: string;
  cards?: string;
  convertApiEnabled?: boolean;
  currentPlan?: string;
  customer?: string;
  defaultContentAttributes?: DefaultContentAttributeSchema[];
  discoveryReason?: string;
  email?: string;
  employeeRange?: string;
  faviconUri?: string;
  isCurrentUserMember?: boolean;
  languages?: string[];
  logo?: string;
  pageDescription?: string;
  pageTitle?: string;
  pdfViewerLicenseKey?: string;
  primaryUsage?: string;
  privacyPolicyLink?: string;
  privacyPolicyName?: string;
  reminderDays?: number;
  orgSubscription?: SubscriptionSchema;
  subscriptionQuantity?: string;
  subscriptionStatus?: string;
  title?: string;
  totalPlanPayment?: string;
  vanityUrl?: string;
  vendor?: VendorSchema;
  vendorTenantId?: string;
  tenantId?: string;
  vendorTenantUrl?: string;
  website?: string;
  orgType?: string;
  crmCorporateCurrency?: string;
  propagateToPartners?: boolean;
}

export interface ContentAttributeValueSchema {
  id?: string;
  value: string;
}

export interface ContentAttributeSchema
  extends Pick<DefaultContentAttributeSchema, 'key'> {
  id: string;
  values: ContentAttributeValueSchema[];
}

export interface ContentAudioSchema {
  downloadable: boolean;
  totalSeconds?: number;
  url: string;
  version: number;
}

export interface ContentDocumentSchema {
  downloadable: boolean;
  filename: string;
  mime: string;
  pages: number;
  pdfUrl: string;
  url: string;
  version: number;

  // nullable
  filesize?: number;
  height?: number;
  width?: number;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/contents/content_embeddable_type.rb
 */
export interface ContentEmbeddableSchema {
  id: string;
  embeddedContent: string;
  url: string;
  version: number;
}

export interface ContentImageSchema {
  downloadable: boolean;
  height: number;
  id: string;
  mime: string;
  predominantColor: string;
  uri: string;
  signedUrl: string;
  version: number;
  width: number;
}

export interface ContentTextSchema {
  html: boolean;
  text: string;
  version: number;
}

export interface ContentUrlSchema {
  iframe: boolean;
  openGraph: RoomOpenGraph;
  url: string;
  version: number;
}

export interface ContentVideoCaptionSchema {
  languageCode?: string;
  language?: string;
  url?: string;
}
export interface ContentVideoSchema {
  captions?: ContentVideoCaptionSchema[];
  downloadable: boolean;
  downloadUrl?: string;
  thumbnails?: ContentVideoThumbnailSchema[];
  totalSeconds?: number;
  uri?: string;
  url: string;
  version: number;
}

export interface ContentVideoThumbnailSchema {
  uri?: string;
  url: string;
}

export type RoomOpenGraph = {
  description: string;
  showInIframe: boolean;
  title: string;
  videoSecureUrl?: string;
};

export interface RoleSchema {
  id: string;
  name: string;
}

export interface StorageProviderAuthSchema {
  id: string;
  label: string;
  name?: string;
  ownerType: string;
  provider?: string;
  sharedWith?: StorageProviderShareSchema[];
}

export interface StorageProviderShareSchema {
  sharedWithId: string;
  sharedWithType: StorageProviderAuthShareType;
  sharedData?: UserSchema | TeamSchema | OrganizationSchema;
}

export interface StorageProviderShareWithSchema {
  user: UserSchema;
  team: TeamSchema;
  organization: OrganizationSchema;
}

export interface StorageProviderSchema {
  id: string;
  connections: StorageProviderAuthSchema[];
  enabled: boolean;
  fileSync: boolean;
  installable: boolean;
  name: string;
  organizationLevelEnabled?: boolean;
  provider: string; // StorageProviders

  // nullable
  link?: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/storage_provider_file_type.rb
 */
export interface StorageProviderFileSchema {
  id: string;
  children?: StorageProviderFileSchema[];
  mimeType: string;
  name: string;
  owner?: string;
  size?: string; // @todo convert to number
  type?: StorageProviderFileType;
  updatedAt?: number;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/storage_provider_file_list_type.rb
 */
export interface StorageProviderFileListSchema {
  files: StorageProviderFileSchema[];
  lazyFolders?: boolean;
}

export interface TagSchema {
  name: string;
  count?: number;
}

export interface UserActivitiesSchema {
  id: string;
  fullName: string;
  activities: any[]; // json objects
}

export interface UserRoleSchema {
  id: string;
  organization: OrganizationSchema;
  role: RoleSchema;

  // nullable
  organizationId?: string;
  roleId?: string;
  rolePermissions?: PermissionSchema[];
  status?: string;
}

export interface InviteUserResult {
  link: string;
  username: string;
}

export type HighlightSchema = Record<string, string[]>;

export interface ContentSchema {
  id: string;
  archived: boolean;
  createdAt: string;
  createdAtEpoch: number;
  createdBy?: UserSchema;
  downloadsCount: number;
  libraryVisible: boolean;
  name: string;
  organization: OrganizationSchema;
  personal: boolean;
  shareable: boolean;
  status: string;
  type: string;
  updateable: boolean;
  updatedAt: string;
  updatedAtEpoch: number;
  updatedBy?: UserSchema;
  user: UserSchema;
  userDownloadsCount: number;
  userViewsCount: number;
  uuid: string;
  version: number;
  views: number;
  parentId: string;

  // nullable
  attributes?: ContentAttributesSchema;
  audio?: ContentAudioSchema;
  audios?: ContentAudioSchema[];
  contact?: ContactSchema;
  cover?: string;
  coverAssetId?: string;
  coverType?: string;
  defaultCover?: string;
  deleted?: boolean;
  deletedAt?: number;
  description?: string;
  document?: ContentDocumentSchema;
  documents?: ContentDocumentSchema[];
  embeddable?: ContentEmbeddableSchema;
  embeddables?: ContentEmbeddableSchema[];
  expired?: boolean;
  fuzzyMatch?: boolean;
  highlight?: HighlightSchema;
  image?: ContentImageSchema;
  images?: ContentImageSchema[];
  isArchived?: boolean;
  isInternal?: boolean;
  link?: string;
  mtData?: MtContentData;
  processingLog?: ContentProcessingLogSchema;
  questionId?: string;
  requestedReference?: RequestedReferenceSchema;
  searchScore?: number;
  sectionHeaderId?: string;
  shared?: number;
  sharedRoomIds?: string[];
  signedCover?: string;
  signedDefaultCover?: string;
  storageProviderData?: StorageProviderDataSchema;
  storageSync?: StorageProviderSyncSchema;
  tags?: string[];
  text?: ContentTextSchema;
  texts?: ContentTextSchema[];
  url?: ContentUrlSchema;
  urls?: ContentUrlSchema[];
  userId?: string;
  video?: ContentVideoSchema;
  videos?: ContentVideoSchema[];
}

export type ContentsSchema = Partial<ContentSchema>[];
export type WelcomeMessageSchema = Partial<ContentSchema>;

/**
 * @see https://github.com/referenceable/referenceable_api/tree/develop/app/graphql/application/types/activity_type.rb
 */
export interface ActivitySchema {
  id: string;
  date: string;
  document: any;
}

/**
 * Schema defining common fields belonging to a physical person. These common
 * fields are then specialized by other schemas such as: User, Contact,
 * ActionPlanCollaborator, RoomCollaborator, etc.
 */
export interface PersonSchema {
  email: string;

  // nullable
  firstName?: string;
  lastName?: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/tree/develop/app/graphql/application/types/user_type.rb
 */
export interface UserSchema extends PersonSchema {
  id: string;
  dealsCount: number;
  ownedRoomsCount: number;
  roleName: string;
  roomsCount: number;
  username: string;

  // nullable
  activities?: ActivitySchema[];
  fullName?: string;
  invite?: InviteUserResult;
  isMtInternalUser?: boolean;
  linkedin?: string;
  marketoId?: string;
  member?: MemberSchema;
  pendingPasswordChange?: boolean;
  phoneNumber?: string;
  photo?: string;
  status?: string;
  subscribedToMarketing?: boolean;
  teams?: TeamSchema[];
  totalRef?: number;
  totalRefCompleted?: number;
  userRoles?: UserRoleSchema[];
  ownedPublishedDraftRooms?: number;
  isManager?: boolean;
}

/**
 * @see https://github.com/referenceable/referenceable_api/tree/develop/app/graphql/application/types/contact_type.rb
 */
export interface ContactSchema extends PersonSchema {
  id: string;

  // nullable
  anonymous?: boolean;
  avatarUri?: string;
  company?: CompanySchema;
  companyId?: string;
  consent?: boolean;
  contactable?: boolean;
  crmId?: string;
  jobTitle?: string;
  linkedin?: string;
  mobilePhone?: string;
  organizationId?: string;
  phoneNumber?: string;
  roomSessions?: RoomSessionSchema[];
  updatedAt?: number;
}

/**
 * @see https://github.com/referenceable/referenceable_api/tree/develop/app/graphql/application/types/company_type.rb
 */
export interface CompanySchema {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;

  // nullable
  crmId?: string;
  organizationId?: string;
  user?: UserSchema;
  userId?: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/tree/develop/app/graphql/application/types/question_type.rb
 */
export interface RequestedReferenceSchema {
  id: string;
  company: CompanySchema;
  companyId: string;
  contact: ContactSchema;
  contactId: string;
  contents: ContentSchema[];
  createdAt: number;
  link: string;
  organization: OrganizationSchema;
  organizationId: string;
  providedReferences: number;
  questionIds: string[];
  questions: QuestionSchema[];
  reminders: number[];
  status: RequestedReferenceStatus;
  user: UserSchema;
}

export type RoomJSONStructureButton = {
  id: string;
};

export type RoomJSONStructureContent = {
  id: string;
  coverAssetId?: string;
  coverSigned?: string;
  coverType?: string;
  coverUri?: string;
  description?: string;
  descriptionVisible?: boolean;
  name?: string;
  nameVisible?: boolean;
};

export interface RoomJSONStructureItem {
  id: string;
  type: RoomStructureItemType;

  // nullable
  coverAssetId?: string;
  coverSigned?: string;
  coverType?: string;
  coverUri?: string;
  customHeader?: boolean;
  description?: string;
  descriptionVisible?: boolean;
  fullWidth?: boolean;
  headerVisible?: boolean;
  icon?: string;
  isDownloadable?: boolean;
  logo?: string;
  name?: string;
  nameVisible?: boolean;
  text?: string;
  uri?: string;
  url?: string;

  // Server-side read enrichment (FolderItemEnricher, backend). Present on
  // Folder-type items when returned from a GraphQL read; must never be
  // persisted back — these are derived/computed, not stored fields.
  itemCount?: number;
  updatedAt?: string;
  depth?: number;
}

export type RoomJSONStructureSchema = {
  buttons?: RoomJSONStructureButton[];
  items: RoomJSONStructureItem[];
  welcome?: string;
};

export interface RoomStructureHeader extends RoomJSONStructureItem {
  name: string;
}

export interface RoomStructureContent extends RoomJSONStructureItem {
  match: Partial<ContentSchema>;
}

export interface RoomStructureFolder extends RoomJSONStructureItem {
  itemCount?: number;
}

export interface RoomNotificationSettingsSchema {
  newVisitAlert: boolean;
  newVisitEmail: boolean;
  nudgeContacts: boolean;
}

/**
 * Folder structure item (similar to RoomJSONStructureItem)
 * Represents content, text boxes, or nested folders within a folder
 */
export interface FolderStructureItem {
  id: string;
  type: 'content' | 'text_box' | 'folder';

  // Content fields
  name?: string;
  description?: string;
  coverUri?: string;
  mediaType?: string;

  // Text box fields
  text?: string;

  // Common fields
  position?: number;
}

/**
 * Folder schema matching backend response
 */
export interface FolderSchema {
  id: string;
  name: string;
  organizationId: number;
  roomId: number;
  templateId: number | null;
  parentFolderId: number | null;
  structure: {
    items: FolderStructureItem[];
  };
  createdById: number;
  updatedById: number | null;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
  depth: number;

  // Related entities
  parentFolder?: {
    id: string;
    name: string;
  } | null;

  childFolders?: Array<{
    id: string;
    name: string;
  }>;

  room: {
    id: string;
    name: string;
    status: string;
  };

  createdBy: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
  };

  updatedBy?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
  } | null;
}

export interface RoomSecuritySchema {
  expiresAt?: number;
  invitedContactsOnly: boolean;
  passcode?: string;
  requiresEmail: boolean;
  requiresExpirationDate: boolean;
  requiresPasscode: boolean;
  verifyEmail: boolean;
}

export interface RoomSettingsSchema extends RoomSecuritySchema {
  layout: RoomLayout;
  language: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/tree/develop/app/graphql/application/types/deal_type.rb
 */
export interface DealSchema {
  id: string;
  name: string;

  // nullable
  amount?: number;
  closeDate?: string;
  company?: CompanySchema;
  companyId?: string;
  contactId?: string;
  contacts?: ContactSchema[];
  createdAt?: number;
  createdById?: string;
  crmId?: string;
  crmOwnerId?: string;
  currency?: string;
  description?: string;
  dspId?: string;
  lastActivityAt?: number;
  nextStep?: string;
  organizationId?: string;
  orgSfdcCorporateCurrency?: string;
  room?: RoomSchema;
  roomId?: string;
  roomUuid?: string;
  stage?: string;
  type?: string;
  updatedAt?: number;
  user?: UserSchema;
  userId?: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/room_type.rb
 */
export interface RoomSchema extends RoomSecuritySchema {
  id: string;
  audience: RoomAudience;
  enableQa: boolean;
  expired: boolean;
  language: string;
  layout: RoomLayout;
  notificationSettings: RoomNotificationSettingsSchema;
  organizationId: string;
  uuid: string;

  // nullable
  actionPlans?: ActionPlansSchema;
  bannerUri?: string;
  bannerAssetId?: string;
  buttons?: SalesActionSchema[];
  buyerCopilotEnabled?: boolean;
  releaseControlBuyerCopilot?: boolean;
  releaseControlDsrFolders?: boolean;
  clientLogoUri?: string;
  clientLogoUrl?: string;
  company?: CompanySchema;
  companyId?: string;
  // primary contact
  contact?: ContactSchema;
  contactId?: string;
  contentIds?: string[];
  contents?: ContentsSchema;
  contentTemplateIds?: string[];
  conversationThreadsCount?: number;
  createdAt?: number;
  createdBy?: UserSchema;
  createdById?: string;
  deal?: DealSchema;
  description?: string;
  domainsFilter?: string[];
  enableContactCard?: boolean;
  enableShare?: boolean;
  exportable?: boolean;
  lastActivityAt?: number;
  link?: string;
  name?: string;
  noOfItems?: number;
  organization?: OrganizationSchema;
  owner?: UserSchema;
  ownerId?: string;
  ownerName?: string;
  permissions?: RoomPermissionsSchema;
  roomCollaborators?: RoomCollaboratorSchema[];
  roomContactsCount?: number;
  roomObjectAclsContents?: string[];
  roomTemplate?: RoomTemplateSchema;
  roomTemplateId?: string;
  roomType?: string;
  signables?: SignablesSchema;
  signablesStructure?: SignablesJSONStructureSchema;
  status?: RoomStatus;
  structure?: RoomJSONStructureSchema;
  thumbnailAssetId?: string;
  thumbnailUri?: string;
  thumbnailUrl?: string;
  totalItems?: number;
  trackActivity?: boolean;
  updatedAt?: number;
  updatedById?: string;
  userLink?: string;
  visitorsCount?: number;
  visitsCount?: number;
  welcomeMessage?: WelcomeMessageSchema;
  welcomeMessageId?: string;
  metadata?: Record<string, any>;
  sourceRoomId?: string;
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/conversation_owner_type.rb
 */
export interface ConversationOwnerSchema {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  photo?: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/tree/develop/app/graphql/application/types/conversation_thread_uploads/conversation_thread_upload_type.rb
 */
export interface ConversationThreadAttachmentSchema {
  id: string;
  organizationId: string;
  conversationThreadId: string;
  uri: string;
  createdAt: number;
  updatedAt: number;

  // nullable
  mime?: string;
  filename?: string;
  filesize?: number;
}

export interface RoomAttachmentSchema
  extends ConversationThreadAttachmentSchema {
  actionPlanId?: string;
  owner: ConversationOwnerSchema;
  taskId?: string;
  type: RoomAttachmentDiscussionType;
}

/**
 * @see https://github.com/referenceable/referenceable_api/tree/develop/app/graphql/application/types/conversation_thread_type.rb
 */
export interface ConversationThreadSchema {
  id: string;
  conversableId: string;
  conversableType: string;
  createdAt: number;
  organizationId: string;
  owner: ConversationOwnerSchema;
  ownerId: string;
  ownerType: string;
  updatedAt: number;

  // nullable
  attachments?: ConversationThreadAttachmentSchema[];
  children?: ConversationThreadSchema[];
  mentions?: ConversationThreadMentions[];
  message?: string;
  parentId?: string;
  subConversableId?: string;
  subConversableType?: string;
}

export interface ConversationThreadMentions extends EnrichContactSchema {
  displayName?: string;
  email?: string;
  entityId: string;
  entityType: ConversationMentionType;
  mobilePhone?: string;
}

export interface DocumentMetadata {
  page: number;
  seen: boolean;
  seconds: number;
}

export interface TextMetadata {
  clicked: boolean;
}

export interface DownloadMetadata {
  contentId: string;
  version?: number;
}

export interface SalesActionInteractionMetadata {
  id: string;
  label: string;
  url: string;
}

/**
 * Expected metadata format for media content analytics, where each key
 * represents a second or a range of seconds for said media and the values
 * are the amount of times that second or range has been played.
 * Examples:
 *  - 1-60: 7 => from second 1 to second 60 the media has been played 7 times
 *  - 61-80: 2 => from second 61 to second 80, it has been played 2 times
 *  - 81 => second 81 has been played once
 */
export interface MediaMetadata {
  [key: string]: number;
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/room_analytic_type.rb
 */
export interface RoomAnalyticSchema {
  id: string;
  contactId: string;
  contentId: string;
  event: TrackEvent;
  measure: number;
  organizationId: string;
  roomId: string;
  updatedAt: number;
  version: number;

  // nullable
  completion?: number;
  metadata?: RoomSessionActivityMetadata;
  roomSessionIds?: string[];
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/room_interaction_type.rb
 */
export interface RoomInteractionSchema {
  id: string;
  contactId: string;
  createdAt: number;
  event: InteractionEvent;
  measure: number;
  organizationId: string;
  roomId: string;
  roomSessionId: string;

  // nullable
  metadata?: SalesActionInteractionMetadata;
}

/**
 * @see https://github.com/referenceable/referenceable_api/tree/develop/app/graphql/application/types/content_report_type.rb
 */
export interface ContentReportSchema {
  contentId: string;
  denominator: number;
  numerator: number;

  // nullable
  content?: Partial<ContentSchema>;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/insights/dollar_attribution_report_type.rb#L0-L1
 */
export interface DollarAttributionReportSchema {
  amount: number;
  contentId: string;
  dealCount: number;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/insights/room_owner_type.rb#L0-L1
 */
export interface RoomOwnerReportSchema {
  totalDeals: number;
  withRooms: number;

  // nullable
  userId?: string;
}

export type ReportSchema = ContentReportSchema[];

export interface SearchEventMetadata {
  results: number;
  term: string;
}

export type EventMetadata = SearchEventMetadata | any;

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/event_type.rb
 */
export interface EventSchema {
  id: string;
  createdAt: number;
  createdAtEpoch: number;
  event: UserActivityEvent;
  meta?: EventMetadata;
  updatedAt: number;
  user: UserSchema;
  userId: string;
}

export interface RoomTemplateSettingsSchema {
  invitedContactsOnly: boolean;
  notificationSettings: RoomNotificationSettingsSchema;
  requiresEmail: boolean;
  requiresExpirationDate: boolean;
  requiresPasscode: boolean;
  thumbnailAssetId?: string;
  thumbnailUri?: string;
  thumbnailUrl?: string;
  verifyEmail: boolean;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/room_template_type.rb
 */
export interface RoomTemplateSchema
  extends Partial<RoomTemplateSettingsSchema> {
  id: string;
  audience: RoomAudience;
  createdAt: number;
  createdById: string;
  enableQa: boolean;
  language: string;
  layout: RoomLayout;
  name: string;
  organizationId: string;
  personal: boolean;
  roomName: string;
  updatedAt: number;
  updatedById: string;
  forPartnerUse: boolean;

  // nullable
  actionPlans?: ActionPlansSchema;
  disabledSinceAllContentsUsed: boolean;
  bannerAssetId?: string;
  bannerUri?: string;
  buttons?: SalesActionSchema[];
  contents?: ContentsSchema;
  cover?: string;
  createdBy?: UserSchema;
  description?: string;
  roomsCount?: number;
  structure?: RoomJSONStructureSchema;
  updatedBy?: UserSchema;
  welcomeMessage?: WelcomeMessageSchema;
  isAutomationTemplate?: boolean;
}

export interface AutomationTemplateData {
  isAutomationApplied: boolean;
  filteredTemplateIds: string[];
}

export interface ConversationContactSchema extends ContactSchema, UserSchema {
  id: string;
  email: string;
}

export interface TeamSchema {
  id: string;
  name: string;
  users?: UserSchema[];
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/action_plans/action_plan_task_owner_type.rb
 */
export interface ActionPlanTaskOwnerSchema {
  ownerId: string;
  ownerType: CollaboratorType;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/action_plans/action_plan_task_type.rb
 */
export interface ActionPlanTaskSchema {
  id: string;
  conversationThreadsCount: number;
  createdAt: number;
  createdById: string;
  milestone: boolean;
  name: string;
  revision: number;
  status: ActionPlanTaskStatus;
  updatedAt: number;

  // nullable
  createdBy?: UserSchema;
  conversationThreads?: ConversationThreadSchema[];
  description?: string;
  dueDate?: string;
  logs?: TrailLogSchema[];
  owners?: ActionPlanTaskOwnerSchema[];
  parentTaskId?: string;
  previousTaskId?: string;
  requester?: ActionPlanCollaboratorSchema;
  subtasks?: ActionPlanTasksSchema;
  updatedById?: string;
  updatedByType?: CollaboratorType;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/action_plans/action_plan_collaborator_type.rb
 */
export interface ActionPlanCollaboratorSchema extends PersonSchema {
  id: string;
  collaboratorType: CollaboratorType;

  // nullable
  avatarUri?: string;
  photo?: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/action_plans/action_plan_type.rb
 */
export interface ActionPlanSchema {
  id: string;
  createdAt: number;
  createdById: string;
  name: string;
  organizationId: string;
  updatedAt: number;
  updatedById: string;
  visibility: ActionPlanVisibility;

  // nullable
  collaborators?: ActionPlanCollaboratorsSchema;
  createdBy?: UserSchema;
  description?: string;
  lastActivityAt?: number;
  tasks?: ActionPlanTasksSchema;
  updatedBy?: UserSchema;
}

export interface ActionPlanInputSchema {
  id: string;
  visibility: ActionPlanVisibility;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/action_plans/action_plan_template_task_input_type.rb
 */
export interface ActionPlanTemplateSubtaskSchema {
  name: string;

  // nullable
  description?: string;
  status?: ActionPlanTaskStatus;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/action_plans/action_plan_template_task_type.rb
 */
export interface ActionPlanTemplateTaskSchema {
  name: string;

  // nullable
  description?: string;
  milestone?: boolean;
  status?: ActionPlanTaskStatus;
  subtasks?: ActionPlanTemplateSubtaskSchema[];
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/action_plans/action_plan_template_type.rb
 */
export interface ActionPlanTemplateSchema {
  id: string;
  actionPlansCount: number;
  createdAt: number;
  createdById: string;
  description?: string;
  name: string;
  organizationId: string;
  tasksCount: number;
  updatedAt: number;
  updatedById: string;

  // nullable
  createdBy?: UserSchema;
  tasks?: ActionPlanTemplateTasksSchema;
  updatedBy?: UserSchema;
}

export type ActionPlansSchema = ActionPlanSchema[];
export type ActionPlanTasksSchema = ActionPlanTaskSchema[];
export type ActionPlanTaskOwnersSchema = ActionPlanTaskOwnerSchema[];
export type ActionPlanCollaboratorsSchema = ActionPlanCollaboratorSchema[];
export type ActionPlanTemplatesSchema = ActionPlanTemplateSchema[];
export type ActionPlanTemplateTasksSchema = ActionPlanTemplateTaskSchema[];

export interface ActionPlanTaskReorderHistoryLogMeta {
  position: ActionPlanTaskReorderPosition;
  taskName: string;
}

export type WhodunnitSchema = Pick<
  ContactSchema,
  'email' | 'firstName' | 'lastName'
>;

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/trail_log_type.rb
 */
export interface TrailLogSchema {
  id: string;
  changes: string;
  date: number;
  entityId: string;
  entityType: TrailLogEntityType;
  event: TrailLogEvent;
  updatedAt: number;

  // nullable
  meta?: ActionPlanTaskReorderHistoryLogMeta;
  whodunnit?: WhodunnitSchema;
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/room_session_type.rb
 */
export interface RoomSessionSchema {
  id: string;
  contactId: string;
  roomId: string;

  // nullable
  agent?: string;
  browser?: string;
  city?: string;
  country?: string;
  countryCode?: string;
  device?: DeviceType;
  formattedTimeSpent?: string;
  ip?: string;
  itemsViewed?: number;
  lastActivityAt?: number;
  platform?: string;
  state?: string;
  stateCode?: string;
  timeSpent?: number;
}

export type RoomSessionActivityMetadata =
  | DocumentMetadata[]
  | DownloadMetadata
  | MediaMetadata
  | TextMetadata;

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/rooms/room_session_activity_type.rb
 */
export interface RoomSessionActivitySchema {
  id: string;
  contentId: string;
  event: TrackEvent;
  measure: number;
  roomId: string;
  updatedAt: number;
  version: number;

  completion?: number;
  createdAt?: number;
  guestId?: string;
  guestType?: CollaboratorType;
  metadata?: RoomSessionActivityMetadata;
  roomGuestId?: string;
  roomSessionId?: string;
}

export interface EnrichContactSchema {
  avatarUri?: string;
  companyId?: number;
  companyName?: string;
  enrichment?: string;
  enrichmentLastSync?: string;
  enrichmentProvider?: string;
  firstName?: string;
  jobTitle?: string;
  lastName?: string;
  linkedin?: string;
  phoneNumber?: string;
}

export interface StorageProviderDataSchema {
  copyLink?: string;
  provider: string; // StorageProviders
  viewLink?: string;
}

export interface StorageProviderSyncSchema {
  newVersion?: StorageProviderNewVersioncSchema;
  preference?: string;
  provider?: string;
  status?: string;
}

export interface StorageProviderNewVersioncSchema {
  id?: string;
  link?: string;
  name?: string;
  version?: string;
}

export interface RoomCollaboratorSchema extends PersonSchema {
  id: string;
  collaboratorId: string;
  collaboratorType: CollaboratorType;
  emailNotification: boolean;
  organizationId: string;
  sellingTeam: boolean;

  // nullable
  jobTitle?: string;
  lastAccessAt?: number;
  linkedin?: string;
  phoneNumber?: string;
  photo?: string;
  roleDescription?: string;
  username?: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/organization_asset_type.rb
 */
export interface OrganizationAssetSchema {
  id: string;
  assetType: OrganizationAssetType;
  createdAt: number;
  createdBy: UserSchema;
  createdById: string;
  deleted: boolean;
  mime: string;
  name: string;
  organizationId: string;
  signedUrl: string;
  updatedAt: number;
  updatedBy: UserSchema;
  uri: string;
  usesCount: number;

  // nullable
  keywords?: string[];
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/report_log_type.rb
 */
export interface ReportLogSchema {
  id: string;
  createdAt: number;
  createdById: string;
  deleted: boolean;
  organizationId: string;
  reportType: ReportType;
  status: string;
  updatedAt: number;

  // nullable
  arguments?: Record<string, any>;
  completedAt?: number;
  downloadedAt?: number;
  filename?: string;
  meta?: Record<string, any>;
  redirectUrl?: string;
  uri?: string;
  url?: string;
}

export interface SubscriptionPlanSchema {
  name: string;
  uuid: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/subscription_type.rb
 */
export interface SubscriptionSchema {
  id: string;
  createdAt: number;
  customerPortal: boolean;
  frequency: BillingPeriod;
  licenses: number;
  licensesSelfServe: boolean;
  plan: SubscriptionPlanSchema;
  pricePerLicense?: number;
  status: string;
  usedLicensesCount: number;

  // nullable
  manageUrl?: string;
  trialEndDate?: number;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/price_type.rb
 */
export interface PriceSchema {
  id: string;
  amount: number;
  frequency: BillingPeriod;
  subscribeUrl: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/plan_type.rb
 */
export interface PlanSchema {
  id: string;
  initialLicenses: number;
  name: string;
  trialDays: number;
  uuid: string;

  // nullable
  prices?: PriceSchema[];
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/section_header_type.rb
 */
export interface SectionHeaderSchema {
  id: string;
  name: string;
  sortOrder: number;

  // nullable
  createdAt?: number;
  createdBy?: UserSchema;
  createdById?: string;
  headerVisible?: boolean;
  icon?: string;
  logoUrl?: string;
  organizationId?: string;
  signedUrl?: string;
  updatedAt?: number;
  updatedBy?: UserSchema;
  updatedById?: string;
  uri?: string;

  // convenience fields to distinguish room section headers from organization section headers
  isCustomHeader?: boolean;
  isRoomHeader?: boolean;
  // set when this was created from a RoomContainerSelector folder selection
  isFolder?: boolean;
}

export interface GroupedSectionHeadersSchema {
  label: string;
  sectionHeaders: SectionHeaderSchema[];
  id: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/tree/develop/app/graphql/application/types/question_type.rb
 */
export interface QuestionSchema {
  id: string;
  canCreateAudios: boolean;
  canCreateImages: boolean;
  canCreateDocuments: boolean;
  canCreateTexts: boolean;
  canCreateVideos: boolean;
  organizationId: string;
  referenceCategory: ReferenceCategorySchema;
  referenceCategoryId: string;
  question: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/reference_category_type.rb
 */
export interface ReferenceCategorySchema {
  id: string;
  name: string;

  // nullable
  questions?: QuestionSchema[];
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/signables/signer_type.rb
 */
export interface SignerSchema {
  id?: string;
  company?: string;
  email?: string;
  jobTitle?: string;
  lastEvent?: string;
  name?: string;
  photo?: string;
  roleName?: string;
  status?: SignerStatus;
  type?: string;
}

export interface SignableProviderSchema {
  id: string;
  enabled: boolean;
  name: string;
  provider: SignableProviders;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/signables/signable_type.rb
 */
export interface SignableSchema {
  id: string;
  createdAt: number;
  name: string;
  pendingSignature: boolean;
  provider: SignableProviders;
  status: SignableStatus;
  syncStatus: SignableSyncStatus;
  updatedAt: number;

  // nullable
  annotations?: SignableAnnotationSchema[];
  canBeEdited?: boolean;
  canBeVoided?: boolean;
  lastSyncAt: number;
  originalFiles?: SignableFileSchema[];
  pdfUrl?: string;
  signableTemplateId?: string;
  signers: SignerSchema[];
  uri?: string;
  url?: string;

  // convenience field that comes from the signables structure
  visible?: boolean;
}

export type SignablesSchema = SignableSchema[];

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/signables/signable_structure_type.rb
 */
export interface SignablesJSONStructureItemSchema {
  id: string;
  name: string;
  visible: boolean;
}

export type SignablesJSONStructureSchema = SignablesJSONStructureItemSchema[];

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/third_parties/docusign/docusign_signer_type.rb
 */
export interface DocusignSignerSchema {
  email?: string;
  name?: string;
  roleName?: string;
  status?: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/third_parties/docusign/envelope_type.rb
 */
export interface DocusignEnvelopeSchema {
  id: string;
  created: number;
  lastModified: number;
  name: string;
  sender: string;

  // nullable
  signers?: DocusignSignerSchema[];
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/signables/signables_annotation_input_type.rb
 */

export interface SignableAnnotationSchema {
  id: string;
  height: number;
  optional: boolean;
  pageNumber: number;
  signerId: string;
  type: SignableFieldType;
  width: number;
  x: number;
  y: number;

  // nullable
  fontSize?: number;
  value?: string;

  // convenience values, populated by the FE
  defaultValue?: string;
  signer?: SignerSchema;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/signables/signable_file_type.rb
 */
export interface SignableFileSchema {
  order: number;
  uri: string;
  url?: string;
}

export interface CollaboratorSchema extends PersonSchema {
  id: string;
  type: CollaboratorType;

  // nullable
  company?: string;
  crmId?: string;
  jobTitle?: string;
  photo?: string;
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/signables/signable_template_type.rb
 */
export interface SignableTemplateSchema {
  id: string;
  annotations: SignableAnnotationSchema[];
  createdAt: number;
  createdById: string;
  description?: string;
  files: SignableFileSchema[];
  name: string;
  organizationId: string;
  signablesCount: number;
  signers: SignerSchema[];
  updatedAt: number;
  updatedById: string;

  // nullable
  createdBy?: UserSchema;
  updatedBy?: UserSchema;
}

export type SignableTemplatesSchema = SignableTemplateSchema[];

export type TranslationData = Record<string, string>;

export interface CrmSummarySchema {
  createdAt: number;

  // nullable
  companiesSyncedTotal?: number;
  contactsSyncedTotal?: number;
  dealsSyncedTotal?: number;
  dealStagesSyncedTotal?: number;
  dealTypesSyncedTotal?: number;
  lastActivityAt?: number;
  usersSyncedTotal?: number;
}

export interface ConsentSchema {
  displayConsent: boolean;
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/oauth/url_config_type.rb
 */
export interface OAuthSchema {
  azureUrl: string;
  googleUrl: string;
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/configurations/config_type.rb
 */
export interface ConfigBootstrapSchema {
  apiUrl: string;
  equipExternalApiUrl: string;
  oauthUrls: OAuthSchema;
  region: string;
}

export interface PermissionSchema {
  permission: Permission;
  value: PermissionValue;
}
export interface RolePermissionSchema extends PermissionSchema {
  roleId: string;
}

export interface RoomPermissionsSchema {
  canEdit?: boolean;
}

export type RolePermissionsSchema = RolePermissionSchema[];

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/rooms/room_guest_reminder_type.rb
 */
export interface RoomGuestReminderSchema {
  status: RoomGuestReminderStatus;
  targetAt: number;
}

interface BasicGuestInfo {
  photo?: string;
  type?: CollaboratorType;
}

export type ContactGuestSchema = BasicGuestInfo &
  Pick<
    ContactSchema,
    | 'id'
    | 'anonymous'
    | 'avatarUri'
    | 'company'
    | 'companyId'
    | 'crmId'
    | 'email'
    | 'firstName'
    | 'jobTitle'
    | 'lastName'
    | 'linkedin'
    | 'mobilePhone'
    | 'phoneNumber'
    | 'updatedAt'
  >;

export type UserGuestSchema = BasicGuestInfo &
  Pick<
    UserSchema,
    | 'id'
    | 'email'
    | 'firstName'
    | 'lastName'
    | 'linkedin'
    | 'phoneNumber'
    | 'username'
  >;

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/rooms/guests/guest_type.rb
 */
export type GuestSchema = ContactGuestSchema | UserGuestSchema;

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/rooms/room_guest_type.rb
 */
export interface RoomGuestSchema {
  id: string;
  guest: GuestSchema;
  guestId: string;
  guestType: CollaboratorType;
  roomId: string;

  // nullable
  analytics?: RoomAnalyticSchema[];
  formattedTimeSpent?: string;
  invitedRoomGuests?: RoomGuestSchema[];
  inviterId?: string;
  inviterType?: CollaboratorType;
  itemsViewed?: number;
  lastActivityAt?: number;
  latestSessionWithLocation?: RoomSessionSchema;
  reminders?: RoomGuestReminderSchema[];
  roomContentsAllowed?: string[];
  roomInteractions?: RoomInteractionSchema[];
  roomSessions?: RoomSessionSchema[];
  timeSpent?: number;
  visits?: string;
  visitsCount?: number;

  /** Copilot chat for this guest in the room (from roomGuests query). */
  copilotChat?: {
    chatId?: string | null;
    chatMessagesCount?: number;
  };
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/guests/guests_input_type.rb
 */
export interface RoomGuestInputSchema {
  id: string;
  guest?: GuestSchema;
  type: CollaboratorType;
}

export type RoomGuestsInputSchema =
  PaginatedResourceInput<RoomGuestInputSchema>;

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/um/group_type.rb
 */
export interface UmGroupSchema {
  id: string;
  name: string;
  numUsers: number;
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/um/user_type.rb
 */
export interface UmUserSchema {
  id: string;
  username: string;

  // nullable
  displayName?: string;
  email?: string;
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/rooms/acl/room_acl_enum_type.rb
 */
export interface AclDomainSchema {
  id: string;
  name: string;
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/resolvers/room_acl_data_resolver.rb
 */
export type RoomAclDataSchema =
  | ContactSchema
  | UmGroupSchema
  | UmUserSchema
  | UserSchema
  | AclDomainSchema;

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/rooms/acl/room_acl_type.rb
 */
export interface RoomAclSchema {
  id: string;
  data: RoomAclDataSchema;
  type: RoomAclType;
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/rooms/acl/room_acls_input_type.rb
 */
export type RoomAclsInputSchema = PaginatedResourceInput<RoomAclSchema> & {
  copyGuests: boolean;
};

export interface RoomObjectAclBaseSchema {
  guestId: string;
  guestType: RoomObjectAclGuestType;
  objectDataId?: string;
  objectDataType?: RoomObjectAclType;
}

export type RoomObjectAclSchema = RoomObjectAclBaseSchema & {
  id: string;
  guest?: GuestSchema;
  roomId?: string;
};

export interface RoomObjectAclInputSchema {
  added: RoomObjectAclBaseSchema[];
  deleted: RoomObjectAclBaseSchema[];
}

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-core-api/-/blob/dev/app/graphql/application/types/contents/content_processing_log_type.rb
 */
export interface ContentProcessingLogSchema {
  processor: ContentProcessors;
  status: ContentProcessingStatus;
}

export interface CrmCustomFieldTypeSchema {
  error: string;
  label: string;
  name: string;
  type?: string;
}

export type ToJSONStructureSchema = {
  field: string;
  entity: string;
};

export interface CrmExportFieldMappingSchema {
  id: string;
  organization?: OrganizationSchema;
  from: string;
  to: ToJSONStructureSchema;
  disabled: boolean;
}

export interface CompanyLogoSchema {
  name: string;
  domain: string;
  logoUrl: string;
}

export interface AISettingsSchema {
  organizationId: string;
  customBuyerCopilotInstructions?: string;
}

export enum ConnectionVariant {
  PRIMARY = 'primary',
  PARTNER = 'partner',
}

export interface InstallableUserIntegrationSchema {
  providerName: IntegrationProvider;
  integrationType: IntegrationType;
  installUrl: string;
  connectionVariant: ConnectionVariant;
}
